import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { serviceurl, imageurl } from 'src/app/constant/constant';
import { WikiTreeResponse, WikiArticle, WikiSearchResult, WikiArticleMediaItem } from '../models/wiki.model';

@Injectable({
  providedIn: 'root'
})
export class WikiApiService {
  private baseUrl: string;
  private treeCache: Map<string, WikiTreeResponse> = new Map();

  private static instance: WikiApiService | null = null;

  constructor(private http: HttpClient) {
    const rawUrl = serviceurl || '';
    this.baseUrl = rawUrl.endsWith('/') ? rawUrl : rawUrl + '/';
    WikiApiService.instance = this;
  }

  /**
   * Retrieves cached navigation tree synchronously if already fetched.
   */
  getCachedTree(mapId?: string): WikiTreeResponse | null {
    const key = mapId || 'default';
    return this.treeCache.get(key) || null;
  }

  /**
   * Fetches the navigation tree for both Universal Lore and World Lore.
   * Automatically caches the tree in-memory so sibling route transitions never reload it.
   * If forceRefresh is true, bypasses the cache to fetch the latest entities/characters.
   */
  getTree(mapId?: string, forceRefresh: boolean = false): Observable<WikiTreeResponse> {
    const key = mapId || 'default';
    if (!forceRefresh && this.treeCache.has(key)) {
      return of(this.treeCache.get(key)!);
    }

    const endpoint = mapId
      ? `${this.baseUrl}api/wiki/tree/${mapId}/`
      : `${this.baseUrl}api/wiki/tree/`;
    return this.http.get<WikiTreeResponse>(endpoint).pipe(
      tap(data => {
        this.treeCache.set(key, data);
        this.treeCache.set('default', data);
        if (data?.world?.map_id) {
          this.treeCache.set(data.world.map_id, data);
        }
        if (data?.worlds) {
          for (const w of data.worlds) {
            if (w.map_id) {
              this.treeCache.set(w.map_id, data);
            }
          }
        }
      })
    );
  }

  /**
   * Invalidate tree cache when world or entities are modified.
   */
  clearTreeCache(): void {
    this.treeCache.clear();
  }

  /**
   * Global static cache invalidation callable from anywhere in the app.
   */
  public static clearCache(): void {
    if (WikiApiService.instance) {
      WikiApiService.instance.clearTreeCache();
    }
  }

  /**
   * Fetches a Universal Lore article (biome, race, politics, etc.)
   */
  getUniversalArticle(category: string, slug: string): Observable<WikiArticle> {
    return this.http.get<WikiArticle>(
      `${this.baseUrl}api/wiki/universal/${category.toLowerCase()}/${slug.toLowerCase()}/`
    );
  }

  /**
   * Fetches a World-specific article (state, city, province, culture, religion, etc.)
   */
  getWorldArticle(mapId: string, entityType: string, slug: string, userId?: number | string): Observable<WikiArticle> {
    let params = new HttpParams();
    if (userId) {
      params = params.set('user_id', String(userId));
    }
    return this.http.get<WikiArticle>(
      `${this.baseUrl}api/wiki/world/${mapId}/${entityType.toLowerCase()}/${slug.toLowerCase()}/`,
      { params }
    );
  }

  /**
   * Fetches the canonical World Overview article for a realm.
   */
  getWorldOverviewArticle(mapId: string, userId?: number | string): Observable<WikiArticle> {
    let params = new HttpParams();
    if (userId) {
      params = params.set('user_id', String(userId));
    }
    return this.http.get<WikiArticle>(
      `${this.baseUrl}api/wiki/world/${mapId}/overview/`,
      { params }
    );
  }

  /**
   * Updates an existing World Wiki article (narrative, lead, infobox, title, caption).
   */
  updateWorldArticle(
    mapId: string,
    entityType: string,
    slug: string,
    payload: {
      name?: string;
      lead_summary?: string;
      content?: string;
      infobox_data?: Record<string, any>;
      image_caption?: string;
    },
    userId?: number | string
  ): Observable<WikiArticle> {
    let params = new HttpParams();
    if (userId) {
      params = params.set('user_id', String(userId));
    }
    return this.http.put<WikiArticle>(
      `${this.baseUrl}api/wiki/world/${mapId}/${entityType.toLowerCase()}/${slug.toLowerCase()}/`,
      { ...payload, user_id: userId },
      { params }
    );
  }

  /**
   * Uploads a photo or document attachment for an article.
   */
  uploadMedia(
    mapId: string,
    entityType: string,
    slug: string,
    formData: FormData,
    userId?: number | string
  ): Observable<WikiArticleMediaItem> {
    let params = new HttpParams();
    if (userId) {
      params = params.set('user_id', String(userId));
      if (!formData.has('user_id')) {
        formData.append('user_id', String(userId));
      }
    }
    return this.http.post<WikiArticleMediaItem>(
      `${this.baseUrl}api/wiki/world/${mapId}/${entityType.toLowerCase()}/${slug.toLowerCase()}/media/`,
      formData,
      { params }
    );
  }

  /**
   * Deletes an uploaded photo or document attachment.
   */
  deleteMedia(mediaId: number, userId?: number | string): Observable<{ status: string; featured_image_url?: string; featured_media_id?: number }> {
    let params = new HttpParams();
    if (userId) {
      params = params.set('user_id', String(userId));
    }
    return this.http.delete<{ status: string; featured_image_url?: string; featured_media_id?: number }>(
      `${this.baseUrl}api/wiki/media/${mediaId}/`,
      { params }
    );
  }

  /**
   * Designates an image as the primary featured infobox picture.
   */
  setFeaturedImage(mediaId: number, userId?: number | string): Observable<{ status: string; featured_image_url: string }> {
    let params = new HttpParams();
    if (userId) {
      params = params.set('user_id', String(userId));
    }
    return this.http.post<{ status: string; featured_image_url: string }>(
      `${this.baseUrl}api/wiki/media/${mediaId}/set-featured/`,
      { user_id: userId },
      { params }
    );
  }

  /**
   * Performs quick search across both Universal and World Lore.
   */
  search(query: string, mapId?: string): Observable<WikiSearchResult[]> {
    let params = new HttpParams().set('q', query);
    if (mapId) {
      params = params.set('map_id', mapId);
    }
    return this.http.get<any>(`${this.baseUrl}api/wiki/search/`, { params }).pipe(
      map(res => {
        if (Array.isArray(res)) return res;
        if (res && Array.isArray(res.results)) return res.results;
        return [];
      })
    );
  }

  /**
   * Resolves media/image paths to full URLs.
   */
  resolveImageUrl(path?: string): string {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('assets/')) {
      return path;
    }
    const cleanMedia = (imageurl || '').endsWith('/') ? imageurl : (imageurl || '') + '/';
    let cleanPath = path.replace(/^\//, '');
    if (cleanMedia.endsWith('/media/') && cleanPath.startsWith('media/')) {
      cleanPath = cleanPath.replace(/^media\//, '');
    }
    return cleanMedia + cleanPath;
  }
}
